import { useCallback, useRef } from 'react';

import { afterNextPaint, getNewRelicApi, withoutUndefined } from '../newRelicPageAction/newRelicPageAction';
import { buildPdfRenderAttributes } from './buildPdfRenderAttributes';
import { readDealSummaryPdfTiming, resetDealSummaryPdfTiming } from './dealSummaryPdfRenderTiming';

const MAX_REPORTED_KEYS = 50;

const reportedKeys = new Set<string>();
const inFlightKeys = new Set<string>();

const rememberReported = (key: string): void => {
    if (reportedKeys.size >= MAX_REPORTED_KEYS) {
        const oldest = reportedKeys.values().next();
        if (!oldest.done) {
            reportedKeys.delete(oldest.value);
        }
    }
    reportedKeys.add(key);
};

export type PdfRenderedTelemetryOptions = {
    pageActionName: string;
    pdfUrl: string | undefined;
    /** Scopes de-duplication, so the same url rendered in a different cohort reports separately. */
    dedupeScope?: string;
    attributes?: Record<string, unknown>;
};

export const usePdfRenderedTelemetry = ({
    pageActionName,
    pdfUrl,
    dedupeScope,
    attributes
}: PdfRenderedTelemetryOptions): (() => void) => {
    const attributesRef = useRef(attributes);
    attributesRef.current = attributes;

    return useCallback(() => {
        if (!pdfUrl) {
            return;
        }

        const key = `${dedupeScope ?? ''}|${pdfUrl}`;
        if (reportedKeys.has(key) || inFlightKeys.has(key)) {
            return;
        }
        inFlightKeys.add(key);

        const { cycleId, marks, context, pdfFetchAttempts } = readDealSummaryPdfTiming();

        afterNextPaint(() => {
            try {
                const newrelic = getNewRelicApi();
                if (!newrelic?.addPageAction) {
                    return;
                }

                const durations = buildPdfRenderAttributes({
                    marks,
                    paintCompleteTime: performance.now(),
                    pdfFetchAttempts
                });
                if (!durations) {
                    return;
                }

                newrelic.addPageAction(pageActionName, withoutUndefined({ ...durations, ...context, ...attributesRef.current }));
                rememberReported(key);
                resetDealSummaryPdfTiming(cycleId);
            } catch {
                // telemetry must never break the UI
            } finally {
                inFlightKeys.delete(key);
            }
        });
    }, [pageActionName, pdfUrl, dedupeScope]);
};

/** TEST-ONLY */
export const __resetReportedPdfKeysForTests = (): void => {
    reportedKeys.clear();
    inFlightKeys.clear();
};

/** TEST-ONLY */
export const __maxReportedPdfKeysForTests = MAX_REPORTED_KEYS;
